/**
 * Story Spine — the narrated-truth rows behind every task surface.
 *
 * One seam, two logs: the server mints these rows from the same events that
 * build the message stream (the Trail). The engine narrates state (closed
 * phase vocabulary), the agent narrates intent (the step line), and the UI
 * renders rows — it never composes a sentence.
 * Spec: docs/superpowers/plans/2026-07-17-story-spine-spec.md
 */

import { z } from 'zod';

/** Closed lifecycle vocabulary — founder-locked like NeedKind.
 *  'stopped' = the user interrupted the turn (founder 07-21: an interrupt must
 *  never read as "Done" — the outcome vocabulary has to be able to say it).
 *  'out_of_credits' = the turn halted because the balance ran out — nobody
 *  pressed Stop (founder 09-03: it read "You stopped this task", "which is
 *  wrong"). Minted only by the AGENT_END seam from the server's own ledger. */
export const STORY_PHASES = [
  'created', 'starting', 'working', 'updating', 'waiting',
  'reconnecting', 'wrapping', 'done', 'stopped', 'out_of_credits', 'failed',
] as const;
export type StoryPhase = (typeof STORY_PHASES)[number];

/** 'lane' is the fast lane's OWN record of a stretch — numbers, closed-list keys
 *  and the box's opaque refs, written once on every ending so the stretch can be
 *  read after the box's logs have rotated (lane.schema.ts LaneStoryPayload). It is
 *  deliberately not a narration kind: no surface renders it, and nothing in it is
 *  a sentence. The lane's user-facing lines are ordinary `step` rows wearing
 *  `lane: 'browser' | 'os'`. */
export const STORY_KINDS = ['phase', 'step', 'ask', 'update', 'piece', 'milestone', 'lane'] as const;
export type StoryKind = (typeof STORY_KINDS)[number];

/** One row of a task's story (camelCase mirror of public.task_story). */
export interface StoryEntry {
  id: string;
  taskId: string;
  userId: string;
  /** Per-task monotonic order, assigned at the server persistence seam. */
  seq: number;
  kind: StoryKind;
  phase?: StoryPhase;
  /** Integration slug for steps/pieces (e.g. 'gmail', 'websearch'). */
  app?: string;
  /** The user-facing sentence. Never a raw tool name. */
  line?: string;
  status?: 'running' | 'ok' | 'error';
  /** Delegation lane id; absent = main lane. */
  lane?: string;
  /** Anchor into the Trail (toolUseId) for receipts. */
  blockRef?: string;
  refId?: string;
  /** Idempotency key (e.g. the toolUseId); unique per task. */
  mintId: string;
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
}

export const StoryEntrySchema = z
  .object({
    id: z.string(),
    taskId: z.string(),
    userId: z.string(),
    seq: z.number().int(),
    kind: z.enum(STORY_KINDS),
    phase: z.enum(STORY_PHASES).optional(),
    app: z.string().optional(),
    line: z.string().optional(),
    status: z.enum(['running', 'ok', 'error']).optional(),
    lane: z.string().optional(),
    blockRef: z.string().optional(),
    refId: z.string().optional(),
    mintId: z.string(),
    startedAt: z.string().optional(),
    endedAt: z.string().optional(),
    createdAt: z.string(),
  })
  .strict();

/** Socket payload for EVENTS.STORY_ENTRY — the push optimization; rows are truth. */
export interface StoryEntryDelta {
  taskId: string;
  entry: StoryEntry;
}

export const StoryEntryDeltaSchema = z
  .object({
    taskId: z.string(),
    entry: StoryEntrySchema,
  })
  .strict();
